import type React from 'react';
import { useCallback, useRef, useState } from 'react';

type MenuPlacement = 'bottom' | 'top';

interface UseScrollAwareMenuPlacementOptions {
  /** Whether the dropdown renders a visible search input above the menu. */
  hasSearchBar?: boolean;
}

interface ScrollAwareMenuPlacement {
  menuPlacement: MenuPlacement;
  maxMenuHeight: number;
  /** Attach to the dropdown wrapper element to measure position on open. */
  placementRef: React.RefObject<HTMLDivElement>;
  /** Call when the dropdown opens to recalculate placement. */
  onOpenMenu: () => void;
}

const MENU_PADDING = 16;
const DEFAULT_MAX_HEIGHT = 300;
// Flip upward when space below can't show at least this many pixels of options.
const FLIP_THRESHOLD = 200;
// The trigger button sits above the dropdown container when opening below,
// and below it when opening above.
const TRIGGER_HEIGHT = 36;
// The search control (input + margin) rendered above the menu inside the container.
const SEARCH_CONTROL_HEIGHT = 54;

function getScrollParent(el: HTMLElement): HTMLElement | null {
  let current = el.parentElement;
  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export default function useScrollAwareMenuPlacement(
  options: UseScrollAwareMenuPlacementOptions = {}
): ScrollAwareMenuPlacement {
  const { hasSearchBar = true } = options;
  const placementRef = useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>('bottom');
  const [maxMenuHeight, setMaxMenuHeight] = useState(DEFAULT_MAX_HEIGHT);

  const onOpenMenu = useCallback(() => {
    const el = placementRef.current;
    if (!el) return;

    const scrollParent = getScrollParent(el);
    if (!scrollParent) {
      setMenuPlacement('bottom');
      setMaxMenuHeight(DEFAULT_MAX_HEIGHT);
      return;
    }

    const scrollRect = scrollParent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const chromeHeight = hasSearchBar ? SEARCH_CONTROL_HEIGHT : 0;

    // Total space from the bottom edge of the control to the scroll parent bottom,
    // minus the search control that sits outside maxMenuHeight in the container.
    const spaceBelow = scrollRect.bottom - elRect.bottom - MENU_PADDING - chromeHeight;
    // When opening above, the container also needs to clear the trigger button.
    const spaceAbove = elRect.top - scrollRect.top - MENU_PADDING - TRIGGER_HEIGHT - chromeHeight;

    if (spaceBelow >= FLIP_THRESHOLD) {
      setMenuPlacement('bottom');
      setMaxMenuHeight(Math.min(spaceBelow, DEFAULT_MAX_HEIGHT));
    } else if (spaceAbove > spaceBelow) {
      setMenuPlacement('top');
      setMaxMenuHeight(Math.min(Math.max(spaceAbove, 0), DEFAULT_MAX_HEIGHT));
    } else {
      setMenuPlacement('bottom');
      setMaxMenuHeight(Math.min(Math.max(spaceBelow, 0), DEFAULT_MAX_HEIGHT));
    }
  }, [hasSearchBar]);

  return { menuPlacement, maxMenuHeight, placementRef, onOpenMenu };
}
